import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { GripVertical, Plus, Scan, Filter, ChevronDown, AlertTriangle, Edit3, Trash2, Lightbulb, CheckCircle2, X, Package, Settings, CornerDownLeft, Lock, Search, Github, RefreshCw, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuItem } from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { featuresApi, epicsApi, releasesApi, productsApi, kanbanRulesApi, teamApi, productTagsApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { cn } from '../../lib/utils';

// Epic color palette
const EPIC_COLORS = [
  { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
  { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-500' },
  { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', dot: 'bg-purple-500' },
  { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', dot: 'bg-green-500' },
  { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-700 dark:text-pink-300', dot: 'bg-pink-500' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-300', dot: 'bg-cyan-500' },
  { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-500' },
  { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300', dot: 'bg-indigo-500' },
];

const DEFAULT_COLUMNS = [
  { id: 'Backlog', title: 'Backlog', color: 'gray' },
  { id: 'In Progress', title: 'In Progress', color: 'yellow' },
  { id: 'Review', title: 'Review', color: 'purple' },
  { id: 'Done', title: 'Done', color: 'green' },
  { id: 'In Production', title: 'In Production', color: 'blue' }
];

const KanbanBoard = ({ selectedNode, productId, goals = [], personas = [], epics = [], onRefresh, onSelectFeature }) => {
  const { getThemeColors } = useSafeMode();
  const { canReorderPriority, canCrudProducts, canUseAI, canField, isCollaborator } = usePermissions();
  const colors = getThemeColors();
  
  // Collaborators can move cards (update status) even without full CRUD
  const canMoveCards = canCrudProducts || canField('features', 'status');
  
  // Core state
  const [features, setFeatures] = useState([]);
  const [releases, setReleases] = useState([]);
  const [selectedReleaseId, setSelectedReleaseId] = useState(null);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // UI state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [epicFilter, setEpicFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tagFilter, setTagFilter] = useState([]);
  const [executionTypeFilter, setExecutionTypeFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [availableTags, setAvailableTags] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showReleaseDropdown, setShowReleaseDropdown] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showColumnRulesModal, setShowColumnRulesModal] = useState(false);
  
  // Optimistic UI state (aligned with Releases pattern)
  const [pendingMoves, setPendingMoves] = useState(new Set());
  
  // Check if a feature is currently being moved
  const isFeaturePending = (featureId) => pendingMoves.has(featureId);
  
  // Audit state
  const [auditResults, setAuditResults] = useState(null);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  // Create epic color map
  const epicColorMap = useMemo(() => {
    const map = {};
    epics.forEach((epic, index) => {
      map[epic.epic_id] = EPIC_COLORS[index % EPIC_COLORS.length];
    });
    return map;
  }, [epics]);

  // Get selected release object
  const selectedRelease = useMemo(() => {
    return releases.find(r => r.release_id === selectedReleaseId);
  }, [releases, selectedReleaseId]);

  const isReadOnly = selectedRelease?.is_closed || false;

  // Load releases, columns, and team members on mount
  useEffect(() => {
    const loadInitialData = async () => {
      if (!productId) return;
      
      setLoading(true);
      try {
        // Load releases, columns, and team in parallel
        const [releasesRes, columnsRes, teamRes] = await Promise.all([
          releasesApi.list(productId),
          productsApi.getKanbanColumns(productId),
          teamApi.list()
        ]);
        
        // Process releases
        const loadedReleases = releasesRes.data || [];
        setReleases(loadedReleases);
        
        // Auto-select first non-closed release (current sprint)
        const activeRelease = loadedReleases.find(r => !r.is_closed);
        if (activeRelease) {
          setSelectedReleaseId(activeRelease.release_id);
        } else if (loadedReleases.length > 0) {
          // If all closed, select the most recent one
          setSelectedReleaseId(loadedReleases[loadedReleases.length - 1].release_id);
        }
        
        // Process columns - ensure system columns (Backlog, Done, In Production) always exist
        if (columnsRes.data?.columns) {
          let cols = columnsRes.data.columns;
          
          // Ensure Backlog is first
          if (!cols.includes('Backlog')) {
            cols = ['Backlog', ...cols];
          }
          
          // Ensure Done exists (before In Production)
          if (!cols.includes('Done')) {
            cols = [...cols.filter(c => c !== 'In Production'), 'Done'];
          }
          
          // Ensure In Production is last (system column)
          cols = cols.filter(c => c !== 'In Production');
          cols.push('In Production');
          
          setColumns(cols.map(col => ({
            id: col,
            title: col,
            color: col === 'Backlog' ? 'gray' : 
                   col === 'In Progress' ? 'yellow' : 
                   col === 'Review' ? 'purple' : 
                   col === 'Done' ? 'green' : 
                   col === 'In Production' ? 'blue' : 'gray'
          })));
        }
        
        // Process team members
        setTeamMembers(teamRes.data?.members || []);
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [productId]);
  
  // Load tags for filtering
  useEffect(() => {
    const loadTags = async () => {
      if (!productId) return;
      try {
        const res = await productTagsApi.list(productId);
        setAvailableTags(res.data.tags || []);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    };
    loadTags();
  }, [productId]);
  
  // Helper to get assignee name
  const getAssigneeName = (assigneeId) => {
    if (!assigneeId) return null;
    const member = teamMembers.find(m => m.user_id === assigneeId);
    if (member) {
      // Return first name or first part of email
      const name = member.name || member.email;
      return name.split(' ')[0].split('@')[0];
    }
    return null;
  };

  // Load features when release selection changes
  useEffect(() => {
    const loadFeatures = async () => {
      if (!selectedReleaseId) {
        setFeatures([]);
        return;
      }
      
      try {
        const response = await releasesApi.getFeatures(selectedReleaseId);
        // Enrich with epic title
        const enrichedFeatures = (response.data || []).map(feat => {
          const epic = epics.find(e => e.epic_id === feat.epic_id);
          return { ...feat, epic_title: epic?.title || 'Unknown Epic' };
        });
        setFeatures(enrichedFeatures);
      } catch (error) {
        console.error('Error loading features:', error);
        setFeatures([]);
      }
    };
    
    loadFeatures();
  }, [selectedReleaseId, epics]);

  // Filter features by epic, search, tags, and execution type
  const filteredFeatures = useMemo(() => {
    return features.filter(f => {
      // Epic filter
      if (epicFilter !== 'all' && f.epic_id !== epicFilter) return false;
      
      // Execution type filter
      if (executionTypeFilter !== 'all') {
        const featureType = f.execution_type || 'engineering';
        if (featureType !== executionTypeFilter) return false;
      }
      
      // Search filter (title or description)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = f.title?.toLowerCase().includes(query);
        const matchesDesc = f.description?.toLowerCase().includes(query);
        if (!matchesTitle && !matchesDesc) return false;
      }
      
      // Tag filter
      if (tagFilter.length > 0) {
        if (!f.tag_ids || !tagFilter.some(tagId => f.tag_ids.includes(tagId))) return false;
      }
      
      // Assignee filter
      if (assigneeFilter !== 'all') {
        if (assigneeFilter === 'unassigned') {
          if (f.assignee_id) return false;
        } else {
          if (f.assignee_id !== assigneeFilter) return false;
        }
      }
      
      return true;
    });
  }, [features, epicFilter, executionTypeFilter, searchQuery, tagFilter, assigneeFilter]);

  // Check if dragging from Backlog is blocked (sprint not started)
  const isBacklogDragBlocked = !selectedRelease?.is_sprint_started;

  // Drag handlers
  const handleDragStart = (e, feat, columnId) => {
    // Block dragging features that are currently being moved
    if (isFeaturePending(feat.feature_id)) {
      e.preventDefault();
      return;
    }
    
    // Block dragging from Backlog if sprint hasn't started
    if (columnId === 'Backlog' && isBacklogDragBlocked) {
      e.preventDefault();
      e.stopPropagation();
      toast.info('Sprint not started', {
        description: 'Start the sprint first before moving features from Backlog'
      });
      return;
    }
    
    if (isReadOnly) {
      e.preventDefault();
      return;
    }
    
    setDraggedItem(feat);
  };

  const handleCardDragOver = (e, status, index, featuresInColumn) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedItem || isReadOnly) return;
    
    const targetFeature = featuresInColumn[index];
    if (targetFeature?.feature_id === draggedItem.feature_id) {
      setDropTarget(null);
      return;
    }
    
    const draggedIndex = featuresInColumn.findIndex(f => f.feature_id === draggedItem.feature_id);
    const isSameColumn = draggedIndex !== -1;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const cardTop = rect.top;
    const cardHeight = rect.height;
    const relativeY = (mouseY - cardTop) / cardHeight;
    
    let position;
    if (isSameColumn) {
      position = draggedIndex < index ? 'after' : 'before';
    } else {
      position = relativeY < 0.4 ? 'before' : 'after';
    }
    
    setDropTarget({ status, index, position });
  };

  const handleCardDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTarget(null);
    }
  };

  const handleColumnDragOver = (e, status) => {
    e.preventDefault();
    if (!draggedItem || isReadOnly) return;
    
    // Set column highlight for visual feedback
    setDragOverColumn(status);
    
    const columnFeats = getFeaturesForColumn(status);
    if (columnFeats.length === 0 || !dropTarget || dropTarget.status !== status) {
      setDropTarget({ status, index: columnFeats.length, position: 'after' });
    }
  };
  
  const handleColumnDragLeave = (e) => {
    // Only clear if we're actually leaving the column (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = async (status) => {
    if (!draggedItem || isReadOnly) return;
    
    const featureId = draggedItem.feature_id;
    const columnFeats = getFeaturesForColumn(status);
    const otherFeats = columnFeats.filter(f => f.feature_id !== featureId);
    
    let newRank;
    
    if (dropTarget && dropTarget.status === status) {
      const { index, position } = dropTarget;
      const targetCard = columnFeats[index];
      const targetIndexInOthers = otherFeats.findIndex(f => f.feature_id === targetCard?.feature_id);
      
      if (otherFeats.length === 0) {
        newRank = 1000;
      } else if (position === 'before') {
        if (targetIndexInOthers <= 0) {
          newRank = (otherFeats[0]?.priority_rank || 1000) - 1000;
        } else {
          const prevRank = otherFeats[targetIndexInOthers - 1]?.priority_rank || 0;
          const currRank = otherFeats[targetIndexInOthers]?.priority_rank || prevRank + 2000;
          newRank = Math.floor((prevRank + currRank) / 2);
        }
      } else {
        if (targetIndexInOthers >= otherFeats.length - 1) {
          newRank = (otherFeats[otherFeats.length - 1]?.priority_rank || 0) + 1000;
        } else {
          const currRank = otherFeats[targetIndexInOthers]?.priority_rank || 0;
          const nextRank = otherFeats[targetIndexInOthers + 1]?.priority_rank || currRank + 2000;
          newRank = Math.floor((currRank + nextRank) / 2);
        }
      }
    } else {
      newRank = otherFeats.length > 0 
        ? (otherFeats[otherFeats.length - 1]?.priority_rank || 0) + 1000 
        : 1000;
    }
    
    const hasStatusChange = draggedItem.status !== status;
    const hasRankChange = draggedItem.priority_rank !== newRank;
    
    if (!hasStatusChange && !hasRankChange) {
      setDraggedItem(null);
      setDropTarget(null);
      setDragOverColumn(null);
      return;
    }
    
    if (!hasStatusChange && hasRankChange && !canReorderPriority) {
      toast.error("You don't have permission to reorder priorities");
      setDraggedItem(null);
      setDropTarget(null);
      setDragOverColumn(null);
      return;
    }
    
    // Handle "In Production" column specially
    const isMovingToProduction = status === 'In Production';
    const isMovingFromProduction = draggedItem.in_production && status !== 'In Production';
    
    const updateData = {};
    
    if (isMovingToProduction) {
      // Moving TO "In Production" - set flag, status stays/becomes Done
      updateData.in_production = true;
      updateData.status = 'Done'; // Backend will also enforce this
    } else if (isMovingFromProduction) {
      // Moving FROM "In Production" back to another column
      updateData.in_production = false;
      updateData.status = status;
    } else {
      // Normal status change
      updateData.status = status;
    }
    
    if (canReorderPriority || hasStatusChange) {
      updateData.priority_rank = newRank;
    }
    
    // STEP 1: Mark feature as pending (visual feedback)
    setPendingMoves(prev => new Set([...prev, featureId]));
    
    // STEP 2: Optimistic update - immediately show in new position
    setFeatures(feats =>
      feats.map(f => f.feature_id === featureId 
        ? { ...f, ...updateData } 
        : f
      )
    );
    
    // Clear drag state immediately for responsive feel
    setDraggedItem(null);
    setDropTarget(null);
    setDragOverColumn(null);
    
    // STEP 3: Make API call in background
    try {
      const response = await featuresApi.update(featureId, updateData);
      const updatedFeature = response.data;
      
      // Handle auto-assign result - update local state with server response
      if (updatedFeature) {
        setFeatures(feats =>
          feats.map(f => f.feature_id === featureId 
            ? { ...f, ...updatedFeature } 
            : f
          )
        );
      }
      
      // Show toast based on what happened
      if (isMovingToProduction) {
        toast.success('Feature promoted to production');
      } else if (isMovingFromProduction) {
        toast.success('Feature moved back from production');
      } else if (updatedFeature?._notification_result?.auto_assigned) {
        const assigneeName = getAssigneeName(updatedFeature.assignee_id);
        toast.success(`Feature moved to ${status}${assigneeName ? ` and assigned to ${assigneeName}` : ''}`);
      } else {
        toast.success(hasStatusChange ? 'Feature moved' : 'Feature reordered');
      }
      
      if (onRefresh) onRefresh();
    } catch (error) {
      // STEP 4: Rollback on failure
      console.error('Error updating feature:', error);
      toast.error(error.response?.data?.detail || 'Failed to update feature');
      setFeatures(feats =>
        feats.map(f => f.feature_id === featureId 
          ? { ...f, status: draggedItem.status, priority_rank: draggedItem.priority_rank, in_production: draggedItem.in_production } 
          : f
        )
      );
    } finally {
      // STEP 5: Always clear pending state
      setPendingMoves(prev => {
        const updated = new Set(prev);
        updated.delete(featureId);
        return updated;
      });
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropTarget(null);
    setDragOverColumn(null);
  };

  // Move feature to parking lot
  const handleMoveToParkingLot = async (feature) => {
    if (!canCrudProducts || isReadOnly) return;
    
    try {
      await releasesApi.unassignFeature(selectedReleaseId, feature.feature_id);
      setFeatures(feats => feats.filter(f => f.feature_id !== feature.feature_id));
      toast.success('Feature moved to Parking Lot');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error moving to parking lot:', error);
      toast.error('Failed to move feature');
    }
  };

  // Add feature to current release
  const handleAddFeature = async () => {
    if (!selectedReleaseId || !canCrudProducts || isReadOnly) return;
    
    // For now, show a toast - in a full implementation, this would open a create feature modal
    toast.info('To add a feature, go to Releases tab and drag from Parking Lot', {
      description: 'Or create a new feature in the Features tab'
    });
  };

  // AI Scanner
  const handleAudit = async () => {
    const epicId = selectedNode?.data?.epic_id || (epicFilter !== 'all' ? epicFilter : null);
    
    if (!epicId) {
      toast.error('Please select an epic to audit', {
        description: 'Use the filter dropdown or select an epic from the hierarchy'
      });
      return;
    }

    setAuditLoading(true);
    try {
      const response = await epicsApi.audit(epicId);
      setAuditResults(response.data);
      setShowAuditDialog(true);
      
      const totalFindings = 
        (response.data.missing_features?.length || 0) + 
        (response.data.features_to_edit?.length || 0) + 
        (response.data.features_to_delete?.length || 0);
      
      if (totalFindings > 0) {
        toast.success('Gap Analysis Complete', { description: `Found ${totalFindings} recommendations` });
      } else {
        toast.success('Epic looks complete!', { description: 'No gaps or issues found' });
      }
    } catch (error) {
      console.error('Error running audit:', error);
      toast.error('Failed to run audit', { description: error.response?.data?.detail || 'Please try again' });
    } finally {
      setAuditLoading(false);
    }
  };

  const getFeaturesForColumn = (columnId) => {
    return filteredFeatures
      .filter(f => {
        // "In Production" column shows features with in_production flag
        if (columnId === 'In Production') {
          return f.in_production === true;
        }
        
        // "Done" column shows Done features that are NOT in production
        if (columnId === 'Done') {
          const featureStatus = f.status || 'Backlog';
          return featureStatus === 'Done' && !f.in_production;
        }
        
        // All other columns filter by status (treat null/undefined as "Backlog")
        const featureStatus = f.status || 'Backlog';
        return featureStatus === columnId;
      })
      .sort((a, b) => {
        const rankA = a.priority_rank ?? Infinity;
        const rankB = b.priority_rank ?? Infinity;
        if (rankA !== rankB) return rankA - rankB;
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateA - dateB;
      });
  };

  // Empty state - no releases
  if (!loading && releases.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <Package className={cn("w-16 h-16 mb-4", colors.textMuted)} />
        <h3 className={cn("text-xl font-semibold mb-2", colors.textPrimary)}>No Releases Yet</h3>
        <p className={cn("text-center mb-6 max-w-md", colors.textSecondary)}>
          Create your first release to start working on features. Features must be assigned to a release to appear on the Kanban board.
        </p>
        <Button 
          onClick={() => window.location.href = `?tab=releases`}
          className={cn("gap-2", colors.accentGradient)}
        >
          <Plus className="w-4 h-4" />
          Go to Release Planning
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full min-h-0">
      {/* Board Header */}
      <div className={cn("p-4 border-b shrink-0", colors.bgCard, colors.borderPrimary)}>
        <div className="flex items-center justify-between gap-4">
          {/* Left side - Release selector and filters */}
          <div className="flex items-center gap-3 flex-1">
            {/* Release Selector */}
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowReleaseDropdown(!showReleaseDropdown)}
                className={cn("gap-2 border min-w-[160px] justify-between", colors.borderPrimary)}
              >
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  <span>{selectedRelease?.name || 'Select Release'}</span>
                  {isReadOnly && <Lock className="w-3 h-3 text-amber-500" />}
                </div>
                <ChevronDown className="w-4 h-4" />
              </Button>
              
              {showReleaseDropdown && (
                <div className={cn("absolute left-0 top-full mt-1 w-64 border rounded-lg shadow-lg z-20", colors.bgCard, colors.borderPrimary)}>
                  <div className="p-2 max-h-80 overflow-y-auto">
                    {/* Active Releases - Started but not closed */}
                    {releases.filter(r => r.is_sprint_started && !r.is_sprint_closed && !r.is_closed).length > 0 && (
                      <>
                        <p className={cn("text-xs font-medium px-3 py-1 flex items-center gap-2", colors.textMuted)}>
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          Active
                        </p>
                        {releases.filter(r => r.is_sprint_started && !r.is_sprint_closed && !r.is_closed).map((release) => (
                          <button
                            key={release.release_id}
                            onClick={() => { setSelectedReleaseId(release.release_id); setShowReleaseDropdown(false); }}
                            className={cn(
                              "w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between",
                              selectedReleaseId === release.release_id ? cn(colors.bgActive, colors.textAccent) : colors.bgHover
                            )}
                          >
                            <span>{release.name}</span>
                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Active</Badge>
                          </button>
                        ))}
                      </>
                    )}
                    
                    {/* Planned Releases - Not started */}
                    {releases.filter(r => !r.is_sprint_started && !r.is_sprint_closed && !r.is_closed).length > 0 && (
                      <>
                        <div className={cn("border-t my-2", colors.borderPrimary)} />
                        <p className={cn("text-xs font-medium px-3 py-1 flex items-center gap-2", colors.textMuted)}>
                          <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                          Planned
                        </p>
                        {releases.filter(r => !r.is_sprint_started && !r.is_sprint_closed && !r.is_closed).map((release) => (
                          <button
                            key={release.release_id}
                            onClick={() => { setSelectedReleaseId(release.release_id); setShowReleaseDropdown(false); }}
                            className={cn(
                              "w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between",
                              selectedReleaseId === release.release_id ? cn(colors.bgActive, colors.textAccent) : colors.bgHover
                            )}
                          >
                            <span>{release.name}</span>
                            <Badge variant="outline" className="text-xs">Planning</Badge>
                          </button>
                        ))}
                      </>
                    )}
                    
                    {/* Closed Releases - Only last 3 */}
                    {releases.filter(r => r.is_sprint_closed || r.is_closed).length > 0 && (
                      <>
                        <div className={cn("border-t my-2", colors.borderPrimary)} />
                        <p className={cn("text-xs font-medium px-3 py-1 flex items-center gap-2", colors.textMuted)}>
                          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                          Closed
                        </p>
                        {releases
                          .filter(r => r.is_sprint_closed || r.is_closed)
                          .sort((a, b) => new Date(b.sprint_closed_at || b.closed_at || 0) - new Date(a.sprint_closed_at || a.closed_at || 0))
                          .slice(0, 3)
                          .map((release) => (
                          <button
                            key={release.release_id}
                            onClick={() => { setSelectedReleaseId(release.release_id); setShowReleaseDropdown(false); }}
                            className={cn(
                              "w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between opacity-60",
                              selectedReleaseId === release.release_id ? cn(colors.bgActive, colors.textAccent) : colors.bgHover
                            )}
                          >
                            <span className="flex items-center gap-2">
                              <Lock className="w-3 h-3" />
                              {release.name}
                            </span>
                            <Badge variant="secondary" className="text-xs">Closed</Badge>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Search */}
            <div className="relative w-64">
              <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
              <Input
                placeholder="Search features..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn("pl-9", colors.bgInput, colors.borderPrimary)}
              />
            </div>
            
            {/* Execution Type Filter */}
            <Select value={executionTypeFilter} onValueChange={setExecutionTypeFilter}>
              <SelectTrigger className={cn("w-[180px]", colors.borderPrimary)} data-testid="kanban-execution-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="engineering">Engineering / Dev</SelectItem>
                <SelectItem value="strategic">Strategic / Ops</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Epic Filter */}
            <Select value={epicFilter} onValueChange={setEpicFilter}>
              <SelectTrigger className={cn("w-[180px]", colors.borderPrimary)}>
                <SelectValue placeholder="All Epics" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="all">All Epics</SelectItem>
                {epics.map((epic) => {
                  const epicColor = epicColorMap[epic.epic_id] || EPIC_COLORS[0];
                  return (
                    <SelectItem key={epic.epic_id} value={epic.epic_id}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${epicColor.dot}`} />
                        <span className="truncate">{epic.title}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {/* Tag Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={cn("gap-2", colors.borderPrimary)}>
                  <Filter className="w-4 h-4" />
                  Tags
                  {tagFilter.length > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">{tagFilter.length}</Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className={cn("w-56", colors.bgCard, colors.borderPrimary)}>
                {tagFilter.length > 0 && (
                  <>
                    <DropdownMenuItem onClick={() => setTagFilter([])}>
                      <X className="w-4 h-4 mr-2" />
                      Clear filters
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {availableTags.map(tag => (
                  <DropdownMenuCheckboxItem
                    key={tag.tag_id}
                    checked={tagFilter.includes(tag.tag_id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setTagFilter([...tagFilter, tag.tag_id]);
                      } else {
                        setTagFilter(tagFilter.filter(id => id !== tag.tag_id));
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                      {tag.name}
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}
                {availableTags.length === 0 && (
                  <div className={cn("px-2 py-4 text-center text-sm", colors.textMuted)}>No tags available</div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Assignee Filter */}
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className={cn("w-[160px]", colors.borderPrimary)} data-testid="kanban-assignee-filter">
                <SelectValue placeholder="All Assignees" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="all">All Assignees</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {teamMembers.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <span className={cn("text-sm", colors.textMuted)}>
              {filteredFeatures.length} features {isReadOnly && <span className="text-amber-500">(read-only)</span>}
            </span>
          </div>
          
          {/* Right side - Actions */}
          <div className="flex items-center gap-2">
            {canUseAI && (selectedNode?.type === 'epic' || epicFilter !== 'all') && (
              <Button
                variant="outline"
                onClick={handleAudit}
                disabled={auditLoading}
                className={cn("gap-2 border", colors.borderPrimary)}
              >
                <Scan className="w-4 h-4" />
                {auditLoading ? 'Scanning...' : 'AI Scanner'}
              </Button>
            )}
            
            {canCrudProducts && (
              <Button 
                variant="outline"
                onClick={() => setShowColumnRulesModal(true)}
                className={cn("gap-2 border", colors.borderPrimary)}
                title="Column Notifications & Auto-Assign"
              >
                <Settings className="w-4 h-4" />
              </Button>
            )}
            
            {canCrudProducts && !isReadOnly && (
              <Button 
                onClick={handleAddFeature}
                className={cn("gap-2", colors.accentGradient)}
              >
                <Plus className="w-4 h-4" />
                Add Feature
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className={cn("text-sm", colors.textMuted)}>Loading...</div>
        </div>
      ) : (
        /* Kanban Columns */
        <div className="flex-1 h-0 overflow-x-auto">
          <div className="flex gap-4 p-4 h-full">
            {columns.map((column) => {
              const columnFeats = getFeaturesForColumn(column.id);
              return (
                <div
                  key={column.id}
                  className={cn(
                    "flex-shrink-0 w-80 h-full flex flex-col rounded-xl border transition-all",
                    colors.bgCard, colors.borderPrimary,
                    dragOverColumn === column.id && "ring-2 ring-blue-500"
                  )}
                  onDragOver={(e) => handleColumnDragOver(e, column.id)}
                  onDragLeave={handleColumnDragLeave}
                  onDrop={() => handleDrop(column.id)}
                >
                  {/* Column Header */}
                  <div className={cn("p-3 border-b shrink-0", colors.borderPrimary)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full bg-${column.color}-500`} />
                        <h4 className={cn("font-medium", colors.textPrimary)}>{column.title}</h4>
                        <Badge variant="secondary" className={cn("text-xs", colors.bgTertiary, colors.textSecondary)}>
                          {columnFeats.length}
                        </Badge>
                        {/* Show lock indicator for Backlog when sprint not started */}
                        {column.id === 'Backlog' && isBacklogDragBlocked && (
                          <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1" title="Start sprint to move features">
                            <Lock className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Cards */}
                  <div className="flex-1 h-0 overflow-y-auto p-3 space-y-3">
                    {columnFeats.map((feat, index) => {
                      const epicColor = epicColorMap[feat.epic_id] || EPIC_COLORS[0];
                      const isDropBefore = dropTarget?.status === column.id && dropTarget?.index === index && dropTarget?.position === 'before';
                      const isDropAfter = dropTarget?.status === column.id && dropTarget?.index === index && dropTarget?.position === 'after';
                      const isDragging = draggedItem?.feature_id === feat.feature_id;
                      const isPending = isFeaturePending(feat.feature_id);
                      // Check if this card is in Backlog and dragging is blocked
                      const isCardDragBlocked = column.id === 'Backlog' && isBacklogDragBlocked;
                      const canDragThisCard = !isReadOnly && !isCardDragBlocked && !isPending;
                      
                      return (
                        <div key={feat.feature_id} className="relative">
                          {isDropBefore && (
                            <div className="absolute -top-1.5 left-0 right-0 h-1 bg-blue-500 rounded-full z-10" />
                          )}
                          <Card
                            draggable={canDragThisCard}
                            onDragStart={(e) => handleDragStart(e, feat, column.id)}
                            onDragOver={(e) => handleCardDragOver(e, column.id, index, columnFeats)}
                            onDragLeave={handleCardDragLeave}
                            onDragEnd={handleDragEnd}
                            className={cn(
                              "p-3 transition-all border hover:shadow-md",
                              colors.bgTertiary,
                              feat.is_blocked 
                                ? "border-red-500 dark:border-red-500 ring-1 ring-red-500/50 animate-pulse-subtle" 
                                : colors.borderPrimary,
                              canDragThisCard && "cursor-grab active:cursor-grabbing",
                              isCardDragBlocked && "cursor-not-allowed opacity-70",
                              isDragging && "opacity-50 scale-95",
                              isPending && "opacity-70 animate-pulse",
                              isReadOnly && "opacity-80"
                            )}
                          >
                            <div className="flex items-start gap-2">
                              {isPending ? (
                                <Loader2 className={cn("w-4 h-4 mt-1 shrink-0 animate-spin text-blue-500")} />
                              ) : canReorderPriority && !isReadOnly && !isCardDragBlocked ? (
                                <GripVertical className={cn("w-4 h-4 mt-1 shrink-0 cursor-move", colors.textMuted)} />
                              ) : isCardDragBlocked ? (
                                <Lock className={cn("w-4 h-4 mt-1 shrink-0", colors.textMuted)} title="Start sprint to enable dragging" />
                              ) : null}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {feat.is_blocked && (
                                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                    )}
                                    <h5 className={cn("font-medium text-sm line-clamp-2", colors.textPrimary)}>
                                      {feat.title}
                                    </h5>
                                    {/* NEW badge for recently split features */}
                                    {feat.is_new_split && (
                                      <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded animate-pulse">
                                        NEW
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    {canCrudProducts && !isReadOnly && !isPending && (
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className={cn("h-6 w-6 shrink-0", colors.bgHover)} 
                                        onClick={() => handleMoveToParkingLot(feat)}
                                        title="Move to Parking Lot"
                                      >
                                        <CornerDownLeft className={cn("w-4 h-4", colors.textMuted)} />
                                      </Button>
                                    )}
                                    {!isPending && (
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className={cn("h-6 w-6 shrink-0", colors.bgHover)} 
                                      onClick={() => onSelectFeature?.(feat)}
                                      title="Edit feature"
                                    >
                                      <Edit3 className={cn("w-4 h-4", colors.textMuted)} />
                                    </Button>
                                    )}
                                  </div>
                                </div>
                                {feat.user_story && (
                                  <p className={cn("text-xs line-clamp-2 mb-3", colors.textSecondary)}>
                                    {feat.user_story}
                                  </p>
                                )}
                                {/* Epic badge + Assignee row */}
                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                  {feat.epic_title && epicFilter === 'all' && (
                                    <div 
                                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${epicColor.bg} ${epicColor.text}`}
                                      onClick={(e) => { e.stopPropagation(); setEpicFilter(feat.epic_id); }}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <div className={`w-1.5 h-1.5 rounded-full ${epicColor.dot}`} />
                                      <span className="truncate max-w-[120px]">{feat.epic_title}</span>
                                    </div>
                                  )}
                                  {/* Assignee pill */}
                                  {feat.assignee_id && getAssigneeName(feat.assignee_id) && (
                                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                      <span className="text-[10px]">👤</span>
                                      <span className="truncate max-w-[80px]">{getAssigneeName(feat.assignee_id)}</span>
                                    </div>
                                  )}
                                  {/* Requirements progress pill */}
                                  {feat.requirements && feat.requirements.length > 0 && (() => {
                                    const completed = feat.requirements.filter(r => typeof r === 'object' ? r.is_completed : false).length;
                                    const total = feat.requirements.length;
                                    return (
                                      <div className={cn(
                                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs",
                                        completed === total 
                                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                                      )}>
                                        <span className="text-[10px]">✓</span>
                                        <span>{completed}/{total}</span>
                                      </div>
                                    );
                                  })()}
                                  {/* External ticket indicator (GitHub, JIRA, Linear, etc.) */}
                                  {feat.external_ticket?.key && (
                                    <a
                                      href={feat.external_ticket.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-800 dark:bg-gray-700 text-white hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                                      title={`${feat.external_ticket.provider === 'github_issues' ? 'GitHub Issue' : 'Ticket'} ${feat.external_ticket.key}`}
                                    >
                                      <Github className="w-3 h-3" />
                                      <span>{feat.external_ticket.key}</span>
                                    </a>
                                  )}
                                  {/* Ticket push failed indicator */}
                                  {feat.external_ticket_push_failed && !feat.external_ticket?.key && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          toast.loading('Retrying ticket creation...', { id: `retry-${feat.feature_id}` });
                                          await releasesApi.pushTicket(feat.feature_id);
                                          toast.success('Ticket created!', { id: `retry-${feat.feature_id}` });
                                          // Refresh to get updated feature
                                          if (onRefresh) onRefresh();
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || 'Failed to create ticket', { id: `retry-${feat.feature_id}` });
                                        }
                                      }}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                                      title={`Ticket push failed: ${feat.external_ticket_push_error || 'Unknown error'}. Click to retry.`}
                                    >
                                      <Github className="w-3 h-3" />
                                      <RefreshCw className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className={cn("text-xs", colors.textMuted)}>#{index + 1}</span>
                                  {feat.effort_size && (
                                    <Badge variant="outline" className="text-xs">{feat.effort_size}</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Card>
                          {isDropAfter && (
                            <div className="absolute -bottom-1.5 left-0 right-0 h-1 bg-blue-500 rounded-full z-10" />
                          )}
                        </div>
                      );
                    })}
                    
                    {columnFeats.length === 0 && (
                      <div className={cn(
                        "text-center py-8 text-sm border-2 border-dashed rounded-lg transition-all",
                        colors.textMuted,
                        dragOverColumn === column.id && "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                      )}>
                        {isReadOnly ? 'No features' : 'Drop features here'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Column Settings Modal */}
      <KanbanColumnSettings
        open={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        productId={productId}
        currentColumns={columns.map(c => c.id)}
        onColumnsUpdated={(newCols) => {
          // Ensure system columns are in correct positions
          let cols = newCols.filter(c => c !== 'In Production');
          if (!cols.includes('Backlog')) cols = ['Backlog', ...cols];
          if (!cols.includes('Done')) cols = [...cols, 'Done'];
          cols.push('In Production'); // Always last
          
          setColumns(cols.map(col => ({
            id: col,
            title: col,
            color: col === 'Backlog' ? 'gray' : 
                   col === 'In Progress' ? 'yellow' : 
                   col === 'Review' ? 'purple' : 
                   col === 'Done' ? 'green' : 
                   col === 'In Production' ? 'blue' : 'gray'
          })));
        }}
      />

      {/* Column Rules Modal (Notifications & Auto-Assign) - Direct access from settings button */}
      <ColumnRulesModal
        open={showColumnRulesModal}
        onClose={() => setShowColumnRulesModal(false)}
        productId={productId}
        columns={columns.map(c => c.id)}
        onRulesUpdated={() => {}}
      />

      {/* AI Scanner Results Dialog */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5 text-blue-600" />
              AI Scanner Results
            </DialogTitle>
            <DialogDescription>
              {auditResults?.summary || `Gap analysis for "${auditResults?.epic_title || 'Epic'}"`}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-6">
              {auditResults?.missing_features?.length > 0 && (
                <div>
                  <h4 className="font-medium text-green-700 dark:text-green-400 flex items-center gap-2 mb-3">
                    <Lightbulb className="w-4 h-4" />
                    Missing Features ({auditResults.missing_features.length})
                  </h4>
                  <div className="space-y-3">
                    {auditResults.missing_features.map((item, index) => (
                      <Card key={index} className="p-3 border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/20">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{item.title}</p>
                        {item.description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{item.description}</p>}
                        <p className="text-xs text-green-600 dark:text-green-400 mt-2">{item.reason}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {auditResults?.features_to_edit?.length > 0 && (
                <div>
                  <h4 className="font-medium text-amber-700 dark:text-amber-400 flex items-center gap-2 mb-3">
                    <Edit3 className="w-4 h-4" />
                    Suggested Improvements ({auditResults.features_to_edit.length})
                  </h4>
                  <div className="space-y-3">
                    {auditResults.features_to_edit.map((item, index) => (
                      <Card key={index} className="p-3 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{item.current_title}</p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">{item.reason}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {auditResults?.features_to_delete?.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-700 dark:text-red-400 flex items-center gap-2 mb-3">
                    <Trash2 className="w-4 h-4" />
                    Out of Scope ({auditResults.features_to_delete.length})
                  </h4>
                  <div className="space-y-3">
                    {auditResults.features_to_delete.map((item, index) => (
                      <Card key={index} className="p-3 border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{item.current_title}</p>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-2">{item.reason}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {(!auditResults?.missing_features?.length && !auditResults?.features_to_edit?.length && !auditResults?.features_to_delete?.length) && (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-gray-400">This epic looks complete!</p>
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAuditDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== KANBAN COLUMN SETTINGS ====================
const KanbanColumnSettings = ({ open, onClose, productId, currentColumns, onColumnsUpdated }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [columns, setColumns] = useState(currentColumns);
  const [newColumn, setNewColumn] = useState('');
  const [loading, setLoading] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [columnRules, setColumnRules] = useState({});
  const requiredColumns = ['Backlog', 'Done', 'In Production'];

  useEffect(() => {
    setColumns(currentColumns);
  }, [currentColumns, open]);

  // Load column rules to show indicators
  useEffect(() => {
    if (open && productId) {
      kanbanRulesApi.get(productId)
        .then(res => setColumnRules(res.data?.rules || {}))
        .catch(() => setColumnRules({}));
    }
  }, [open, productId]);

  // Helper to check if column has rules
  const getColumnRuleIndicators = (col) => {
    const rules = columnRules[col] || {};
    const hasNotify = rules.auto_notify?.length > 0;
    const hasAssign = !!rules.auto_assign;
    const hasClear = !!rules.clear_assignee;
    return { hasNotify, hasAssign, hasClear };
  };

  const handleAddColumn = () => {
    if (!newColumn.trim()) return;
    if (columns.includes(newColumn.trim())) {
      toast.error('Column already exists');
      return;
    }
    // Insert before Done (Done and In Production stay at the end)
    const doneIndex = columns.indexOf('Done');
    const newCols = [...columns];
    newCols.splice(doneIndex, 0, newColumn.trim());
    setColumns(newCols);
    setNewColumn('');
  };

  const handleRemoveColumn = (col) => {
    if (requiredColumns.includes(col)) {
      toast.error(`"${col}" is required and cannot be removed`);
      return;
    }
    setColumns(columns.filter(c => c !== col));
  };

  // Drag and drop handlers for reordering
  const handleDragStart = (e, col, index) => {
    // Don't allow dragging Backlog (first), Done, or In Production (last two)
    if (col === 'Backlog' || col === 'Done' || col === 'In Production') {
      e.preventDefault();
      return;
    }
    setDraggedColumn({ col, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (!draggedColumn) return;
    
    // Don't allow dropping at position 0 (Backlog) or last two positions (Done, In Production)
    if (index === 0 || index >= columns.length - 2) {
      setDragOverIndex(null);
      return;
    }
    
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (!draggedColumn || targetIndex === 0 || targetIndex === columns.length - 1) {
      setDraggedColumn(null);
      setDragOverIndex(null);
      return;
    }

    const newColumns = [...columns];
    const [removed] = newColumns.splice(draggedColumn.index, 1);
    
    // Adjust target index if dragging from before to after
    const adjustedIndex = draggedColumn.index < targetIndex ? targetIndex - 1 : targetIndex;
    
    // Ensure we don't place before Backlog (0) or in last 2 positions (Done, In Production)
    const finalIndex = Math.max(1, Math.min(adjustedIndex, columns.length - 3));
    newColumns.splice(finalIndex, 0, removed);
    
    setColumns(newColumns);
    setDraggedColumn(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Ensure system columns are in correct positions before saving
      let colsToSave = columns.filter(c => c !== 'In Production');
      if (!colsToSave.includes('Backlog')) colsToSave = ['Backlog', ...colsToSave];
      if (!colsToSave.includes('Done')) colsToSave = [...colsToSave, 'Done'];
      colsToSave.push('In Production'); // Always last
      
      const res = await productsApi.updateKanbanColumns(productId, colsToSave);
      if (res.data.features_moved_to_backlog > 0) {
        toast.info(`${res.data.features_moved_to_backlog} features moved to Backlog`);
      }
      toast.success('Kanban columns updated');
      onColumnsUpdated(colsToSave);
      onClose();
    } catch (error) {
      console.error('Error updating columns:', error);
      toast.error(error.response?.data?.detail || 'Failed to update columns');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Customize Kanban Columns</DialogTitle>
          <DialogDescription>
            Drag to reorder, add, or remove columns. Backlog and Done are required.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            {columns.map((col, index) => {
              const isRequired = requiredColumns.includes(col);
              const isDragging = draggedColumn?.col === col;
              const isDragOver = dragOverIndex === index;
              const canDrag = !isRequired;
              
              const { hasNotify, hasAssign, hasClear } = getColumnRuleIndicators(col);
              
              return (
                <div
                  key={col}
                  draggable={canDrag}
                  onDragStart={(e) => handleDragStart(e, col, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "flex items-center justify-between p-2 rounded-md border transition-all",
                    colors.bgTertiary, colors.borderPrimary,
                    canDrag && "cursor-grab active:cursor-grabbing",
                    isDragging && "opacity-50 scale-95",
                    isDragOver && "ring-2 ring-blue-500 ring-offset-1"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {canDrag && (
                      <GripVertical className={cn("w-4 h-4 flex-shrink-0", colors.textMuted)} />
                    )}
                    <span className={cn("text-sm", colors.textPrimary)}>{col}</span>
                    {/* Rule indicators */}
                    {(hasNotify || hasAssign || hasClear) && (
                      <div className="flex items-center gap-1 ml-1">
                        {hasNotify && (
                          <span className="text-xs" title="Auto-notify enabled">🔔</span>
                        )}
                        {hasAssign && (
                          <span className="text-xs" title="Auto-assign enabled">👤</span>
                        )}
                        {hasClear && (
                          <span className="text-xs" title="Clear assignment enabled">🚫</span>
                        )}
                      </div>
                    )}
                  </div>
                  {!isRequired ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleRemoveColumn(col)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Required</Badge>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className="flex gap-2">
            <Input
              value={newColumn}
              onChange={(e) => setNewColumn(e.target.value)}
              placeholder="New column name"
              onKeyDown={(e) => e.key === 'Enter' && handleAddColumn()}
              className={cn(colors.bgInput, colors.borderPrimary)}
            />
            <Button onClick={handleAddColumn} variant="outline">Add</Button>
          </div>
          
          <p className={cn("text-xs", colors.textMuted)}>
            Drag columns to reorder. Removing a column moves all its features to Backlog.
          </p>
          
          {/* Column Rules Button */}
          <div className={cn("pt-4 border-t", colors.borderPrimary)}>
            <Button 
              variant="outline" 
              className="w-full gap-2"
              onClick={() => setShowRulesModal(true)}
            >
              <AlertTriangle className="w-4 h-4" />
              Configure Notifications & Auto-Assign
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading} className={colors.accentGradient}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
      
      {/* Column Rules Modal */}
      <ColumnRulesModal
        open={showRulesModal}
        onClose={() => setShowRulesModal(false)}
        productId={productId}
        columns={columns}
        onRulesUpdated={(newRules) => setColumnRules(newRules)}
      />
    </Dialog>
  );
};


// ==================== COLUMN RULES MODAL (Notifications & Auto-Assign) ====================
const ColumnRulesModal = ({ open, onClose, productId, columns, onRulesUpdated }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [rules, setRules] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedColumn, setExpandedColumn] = useState(null);

  // Load rules and team members
  useEffect(() => {
    if (open) {
      const loadData = async () => {
        setLoading(true);
        try {
          const [rulesRes, teamRes] = await Promise.all([
            kanbanRulesApi.get(productId),
            teamApi.list()
          ]);
          setRules(rulesRes.data?.rules || {});
          // Team API returns { members: [...], counts: {...} }
          setTeamMembers(teamRes.data?.members || []);
        } catch (err) {
          console.error('Error loading column rules:', err);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [open, productId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await kanbanRulesApi.update(productId, rules);
      toast.success('Column rules saved');
      if (onRulesUpdated) onRulesUpdated(rules);
      onClose();
    } catch (err) {
      console.error('Error saving rules:', err);
      toast.error('Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  const updateColumnRule = (column, field, value) => {
    setRules(prev => ({
      ...prev,
      [column]: {
        ...prev[column],
        [field]: value
      }
    }));
  };

  const toggleNotifyUser = (column, userId) => {
    const currentNotify = rules[column]?.auto_notify || [];
    const newNotify = currentNotify.includes(userId)
      ? currentNotify.filter(id => id !== userId)
      : [...currentNotify, userId];
    updateColumnRule(column, 'auto_notify', newNotify);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Column Notifications & Auto-Assign</DialogTitle>
          <DialogDescription>
            Configure automatic notifications and assignments when features enter each column.
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <span className={colors.textMuted}>Loading...</span>
          </div>
        ) : (
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-3 py-4">
              {columns.map(column => {
                const columnRules = rules[column] || {};
                const isExpanded = expandedColumn === column;
                
                return (
                  <div 
                    key={column}
                    className={cn("border rounded-lg overflow-hidden", colors.borderPrimary)}
                  >
                    {/* Column Header */}
                    <button
                      className={cn(
                        "w-full p-3 flex items-center justify-between text-left",
                        colors.bgTertiary,
                        "hover:brightness-95"
                      )}
                      onClick={() => setExpandedColumn(isExpanded ? null : column)}
                    >
                      <div className="flex items-center gap-2">
                        <span className={cn("font-medium", colors.textPrimary)}>{column}</span>
                        {(columnRules.auto_notify?.length > 0 || columnRules.auto_assign || columnRules.clear_assignee) && (
                          <Badge variant="secondary" className="text-xs">
                            {columnRules.auto_notify?.length > 0 && `${columnRules.auto_notify.length} notify`}
                            {columnRules.auto_notify?.length > 0 && (columnRules.auto_assign || columnRules.clear_assignee) && ' + '}
                            {columnRules.auto_assign && 'auto-assign'}
                            {columnRules.clear_assignee && 'clear assign'}
                          </Badge>
                        )}
                      </div>
                      <ChevronDown className={cn(
                        "w-4 h-4 transition-transform",
                        colors.textMuted,
                        isExpanded && "rotate-180"
                      )} />
                    </button>
                    
                    {/* Column Rules */}
                    {isExpanded && (
                      <div className={cn("p-4 space-y-4 border-t", colors.borderPrimary)}>
                        {/* Auto-Notify */}
                        <div>
                          <label className={cn("text-sm font-medium block mb-2", colors.textSecondary)}>
                            🔔 Auto-Notify (when feature enters this column)
                          </label>
                          <div className="space-y-1">
                            {teamMembers.map(member => (
                              <label 
                                key={member.user_id} 
                                className={cn(
                                  "flex items-center gap-2 p-2 rounded cursor-pointer",
                                  colors.bgHover
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={columnRules.auto_notify?.includes(member.user_id) || false}
                                  onChange={() => toggleNotifyUser(column, member.user_id)}
                                  className="rounded"
                                />
                                <span className={cn("text-sm", colors.textPrimary)}>
                                  {member.name || member.email}
                                </span>
                                <Badge variant="outline" className="text-xs ml-auto">{member.role}</Badge>
                              </label>
                            ))}
                            {teamMembers.length === 0 && (
                              <p className={cn("text-sm", colors.textMuted)}>No team members found</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Assignment Options */}
                        <div>
                          <label className={cn("text-sm font-medium block mb-2", colors.textSecondary)}>
                            👤 Assignment Action
                          </label>
                          
                          {/* Clear Assignment Option */}
                          <label className={cn(
                            "flex items-center gap-2 p-2 rounded cursor-pointer mb-2",
                            colors.bgHover
                          )}>
                            <input
                              type="checkbox"
                              checked={columnRules.clear_assignee || false}
                              onChange={(e) => {
                                updateColumnRule(column, 'clear_assignee', e.target.checked);
                                if (e.target.checked) {
                                  updateColumnRule(column, 'auto_assign', null);
                                }
                              }}
                              className="rounded"
                            />
                            <span className={cn("text-sm", colors.textPrimary)}>
                              Clear assignment when entering this column
                            </span>
                          </label>
                          
                          {/* Auto-Assign (disabled if clear is checked) */}
                          {!columnRules.clear_assignee && (
                            <>
                              <label className={cn("text-xs font-medium block mb-1 mt-3", colors.textMuted)}>
                                Or auto-assign to:
                              </label>
                              <select
                                value={columnRules.auto_assign || ''}
                                onChange={(e) => updateColumnRule(column, 'auto_assign', e.target.value || null)}
                                className={cn(
                                  "w-full p-2 rounded-md border text-sm",
                                  colors.bgInput, colors.borderPrimary, colors.textPrimary
                                )}
                              >
                                <option value="">No auto-assign</option>
                                {teamMembers.map(member => (
                                  <option key={member.user_id} value={member.user_id}>
                                    {member.name || member.email} ({member.role})
                                  </option>
                                ))}
                              </select>
                            </>
                          )}
                        </div>
                        
                        {/* Override Assignee Option */}
                        {columnRules.auto_assign && !columnRules.clear_assignee && (
                          <div>
                            <label className={cn(
                              "flex items-center gap-2 p-2 rounded cursor-pointer",
                              colors.bgHover
                            )}>
                              <input
                                type="checkbox"
                                checked={columnRules.override_assignee !== false}
                                onChange={(e) => updateColumnRule(column, 'override_assignee', e.target.checked)}
                                className="rounded"
                              />
                              <span className={cn("text-sm", colors.textPrimary)}>
                                Override existing assignee
                              </span>
                            </label>
                            <p className={cn("text-xs mt-1 pl-6", colors.textMuted)}>
                              If unchecked, auto-assign only applies when no one is assigned
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className={colors.accentGradient}>
            {saving ? 'Saving...' : 'Save Rules'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default KanbanBoard;
